/**
 * «Звонок по сделке» на вкладке «Разговор»: номер сделки → карточка (имя — проверенное, номер, салон, когда
 * поставим следующую обработку, почему звонить нельзя) → «Позвонить по сделке». Звоним только по сделкам
 * «Прокат» «В работе»; модель, голос и режим — из формы стенда. После звонка робот сам ставит дату
 * следующей обработки по правилу Резерва. Спека `docs/specs/2026-10-27-voice-deal-call-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { type LabRequest, labDealCall, labDealQuery } from "@/lib/voiceLab";

import { телефон } from "./VoiceLabJournal";

const control =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";

function дата(iso: string | null): string {
  if (!iso) return "—";
  const [d, t] = iso.split("T");
  const [y, m, dd] = (d ?? "").split("-");
  return `${dd}.${m}.${y?.slice(2)}${t ? ` ${t.slice(0, 5)}` : ""}`;
}

export function VoiceLabDeal({
  body,
  disabled,
  onCalled,
}: {
  body: Pick<LabRequest, "model" | "mode" | "voice" | "speed" | "turn"> | null;
  disabled: boolean;
  onCalled: (r: { room: string; phone: string }) => void;
}) {
  const [ввод, setВвод] = useState("");
  const [id, setId] = useState<number | null>(null);
  const [звоним, setЗвоним] = useState(false);
  const [ошибка, setОшибка] = useState<string | null>(null);
  const q = useQuery(labDealQuery(id));
  const к = q.data;

  async function позвонить() {
    if (!к || !body || звоним) return;
    setОшибка(null);
    setЗвоним(true);
    try {
      onCalled(await labDealCall(к.deal_id, body));
    } catch (e) {
      setОшибка(e instanceof Error ? e.message : String(e));
      void q.refetch();
    } finally {
      setЗвоним(false);
    }
  }

  return (
    <div
      className="flex max-w-3xl flex-col gap-2 rounded-[var(--radius-ui)] border border-border p-3 text-[12.5px]"
      data-annot="звонок по сделке"
    >
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const n = Number(ввод.replace(/\D/g, ""));
          setОшибка(null);
          setId(n > 0 ? n : null);
          if (n > 0 && n === id) void q.refetch();
        }}
      >
        <label className="flex flex-col gap-1">
          Звонок по сделке «В работе»
          <input
            value={ввод}
            onChange={(e) => setВвод(e.target.value)}
            inputMode="numeric"
            placeholder="номер сделки"
            className={`${control} w-40`}
          />
        </label>
        <button
          type="submit"
          className="h-8 rounded-[var(--radius-ui)] border border-border px-3 hover:bg-bg-hover"
        >
          Проверить
        </button>
        {q.isFetching && (
          <span className="text-fg-muted">читаю сделку в Bitrix…</span>
        )}
      </form>
      {q.isError && (
        <div className="text-err">
          {q.error instanceof Error ? q.error.message : "Сделка не загрузилась"}
        </div>
      )}
      {к && (
        <>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            <span className="text-fg-muted">Сделка</span>
            <span>
              <a
                href={`/deals/${к.deal_id}`}
                className="text-accent hover:underline"
              >
                {к.deal_id}
              </a>{" "}
              · {к.title} · стадия {к.stage}
            </span>
            <span className="text-fg-muted">Имя</span>
            <span>
              {к.name ? (
                <>
                  <b>{к.name}</b>
                  {к.name_raw !== к.name && (
                    <span className="text-fg-muted">
                      {" "}
                      (в карточке «{к.name_raw}»)
                    </span>
                  )}
                </>
              ) : (
                <span className="text-warn">
                  в карточке «{к.name_raw || "пусто"}» — не имя: робот не
                  называет по имени, перед записью спросит
                </span>
              )}
            </span>
            <span className="text-fg-muted">Номер</span>
            <span>{к.phone ? телефон(String(к.phone)) : "—"}</span>
            <span className="text-fg-muted">Салон</span>
            <span>{к.location_name ?? "—"}</span>
            <span className="text-fg-muted">Заявка · мероприятие</span>
            <span>
              {дата(к.request_date)} ·{" "}
              {к.event_date ? дата(к.event_date) : "даты мероприятия нет"}
            </span>
            <span className="text-fg-muted">Обработка</span>
            <span>
              сейчас {дата(к.next_work_now)} → после звонка{" "}
              {дата(к.next_work.at)} ({к.next_work.rule}; звонков с этим —{" "}
              {к.calls})
            </span>
          </div>
          {к.blockers.map((b) => (
            <div key={b} className="text-err">
              ✕ {b}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={позвонить}
              disabled={disabled || !body || звоним || к.blockers.length > 0}
              className="h-8 rounded-[var(--radius-ui)] border border-accent px-3 font-medium text-accent disabled:opacity-50"
            >
              {звоним ? "Набираем…" : "Позвонить по сделке"}
            </button>
            <span className="text-fg-muted">
              запись, перезвон, отказ и дата обработки — в эту сделку; всё — в
              «Журнале действий» звонка
            </span>
          </div>
        </>
      )}
      {ошибка && <div className="text-err">{ошибка}</div>}
    </div>
  );
}
